import { getQueryClient } from "@verbleif/lib/src/composables/Query/queryClient";
import { localeState } from "@verbleif/lib/src/translations/localeState";
import { onScopeDispose, watch } from "vue";

/** Persist buster for the web query cache. A different locale discards the stored client. */
export function localePersistBuster(locale: string): string {
  return locale.toLowerCase();
}

function resetLocalizedQueries(): void {
  void getQueryClient().resetQueries();
}

let installed = false;

/**
 * Clears the TanStack cache when the UI locale actually changes.
 * Call once from the root App setup, before the immediate user-locale watch.
 *
 * `persistQueryClient` hydrates on a microtask queued at web startup and only
 * then subscribes to cache writes. A locale change during that setup is deferred
 * until after that microtask, so the restore cannot put the previous language back.
 */
export function useLocaleQueryReset(): void {
  if (installed) {
    return;
  }
  installed = true;
  onScopeDispose(() => {
    installed = false;
  });

  let normalised = localePersistBuster(localeState.value);
  let pastStartupRestore = false;
  queueMicrotask(() => {
    pastStartupRestore = true;
  });

  watch(localeState, (next) => {
    const nextNormalised = localePersistBuster(next);
    if (nextNormalised === normalised) {
      return;
    }
    normalised = nextNormalised;
    if (!pastStartupRestore) {
      queueMicrotask(resetLocalizedQueries);
      return;
    }
    resetLocalizedQueries();
  }, { flush: "sync" });
}
